import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useTable, useSortBy, usePagination } from 'react-table'
import {
  Table,
  Row,
  Col,
  Pagination,
  InputGroup,
  FormControl,
  Dropdown,
  DropdownButton,
} from 'react-bootstrap'

const useResultTable = ({ initialData, columns, ...rest }, ...plugins) => {
  const [data, setData] = useState(initialData)
  const skipPageResetRef = useRef()

  const updateData = useCallback(newData => {
    // When data gets updated with this function, set a flag
    // to disable all of the auto resetting
    skipPageResetRef.current = true

    setData(newData)
  }, [])

  useEffect(() => {
    // After the table has updated, always remove the flag
    skipPageResetRef.current = false
  })

  const table = useTable(
    {
      data,
      columns,
      autoResetPage: !skipPageResetRef.current,
      autoResetExpanded: !skipPageResetRef.current,
      autoResetGroupBy: !skipPageResetRef.current,
      autoResetSelectedRows: !skipPageResetRef.current,
      autoResetSortBy: !skipPageResetRef.current,
      autoResetFilters: !skipPageResetRef.current,
      autoResetRowState: !skipPageResetRef.current,
      ...rest,
    },
    useSortBy,
    usePagination,
    ...plugins
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = table

  const render = () => (
    <>
      <Table {...getTableProps()} size="sm" responsive className="mb-4">
        <thead className="table-light">
          {
            // Loop over the header rows
            headerGroups.map(headerGroup => (
              // Apply the header row props
              <tr {...headerGroup.getHeaderGroupProps()}>
                {
                  // Loop over the headers in each row
                  headerGroup.headers.map(column => (
                    // Apply the header cell props
                    <th
                      className="align-middle user-select-none"
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      scope="col"
                    >
                      {
                        // Render the header
                        column.render('Header')
                      }

                      {
                        <span
                          className={column.isSorted ? 'visible' : 'invisible'}
                        >
                          {column.isSortedDesc ? ' ▼' : ' ▲'}
                        </span>
                      }
                    </th>
                  ))
                }
              </tr>
            ))
          }
        </thead>
        {/* Apply the table body props */}
        <tbody {...getTableBodyProps()}>
          {
            // Loop over the table rows
            page.map(row => {
              // Prepare the row for display
              prepareRow(row)
              return (
                // Apply the row props
                <tr {...row.getRowProps()}>
                  {
                    // Loop over the row's cells
                    row.cells.map(cell => {
                      // Apply the cell props
                      return (
                        <td {...cell.getCellProps()} className="py-2">
                          {
                            // Render the cell contents
                            cell.render('Cell')
                          }
                        </td>
                      )
                    })
                  }
                </tr>
              )
            })
          }
        </tbody>
      </Table>
      {pageCount >= 1 && (
        <Row className="align-items-center mb-3">
          <Col>
            <Pagination size="sm" className="d-inline-flex me-2 mb-sm-0">
              <Pagination.First
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              />
              <Pagination.Prev
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              />
              <Pagination.Item active>{pageIndex + 1}</Pagination.Item>
              <Pagination.Next
                onClick={() => nextPage()}
                disabled={!canNextPage}
              />
              <Pagination.Last
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              />
            </Pagination>
            <span>
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>
            </span>
          </Col>
          <Col xs="auto">
            <InputGroup>
              <InputGroup.Text>Go to page</InputGroup.Text>
              <FormControl
                size="sm"
                className="me-1"
                aria-label="Selected Result Page"
                type="number"
                defaultValue={pageIndex + 1}
                onChange={e => {
                  gotoPage(
                    e.target.value ? Number(e.target.value) - 1 : pageIndex
                  )
                }}
              />
              <DropdownButton
                variant="outline-primary"
                title={`Show ${pageSize}`}
                align="end"
              >
                {
                  // TODO: Maybe make these dynamic?
                  [10, 20, 30, 40, 50].map(pageSize => (
                    <Dropdown.Item
                      key={pageSize}
                      onClick={() => setPageSize(pageSize)}
                    >{`Show ${pageSize}`}</Dropdown.Item>
                  ))
                }
              </DropdownButton>
            </InputGroup>
          </Col>
        </Row>
      )}
    </>
  )

  return { ...table, updateData, render }
}

export { useResultTable }
